// Cash Cockpit + Planned Events shared types

export const EVENT_TYPES = ['Tax', 'Salary', 'Dividend', 'BigPayment', 'Loan', 'Intercompany', 'AR', 'AP', 'Other'] as const;
export type EventType = typeof EVENT_TYPES[number];

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
    Tax: 'Tax Payment',
    Salary: 'Salary',
    Dividend: 'Dividend',
    BigPayment: 'Big Payment',
    Loan: 'Loan',
    Intercompany: 'Intercompany Payment',
    AR: 'AR Inflow (expected)',
    AP: 'AP Outflow (expected)',
    Other: 'Other',
};

export const CONFIDENCES = ['High', 'Medium', 'Low'] as const;
export type Confidence = typeof CONFIDENCES[number];

export const RECURRENCE_PATTERNS = ['Monthly', 'Quarterly', 'Annual'] as const;
export type RecurrencePattern = typeof RECURRENCE_PATTERNS[number];

// Currencies selectable in the Planned Events editor and Cash Outlook toggle.
// Order puts Swiss-centric pairs first.
export const SUPPORTED_CURRENCIES = [
    'CHF', 'EUR', 'USD', 'GBP', 'JPY', 'CNY',
    'TRY', 'PLN', 'SEK', 'NOK', 'DKK', 'CZK', 'HUF', 'AUD', 'CAD',
] as const;
export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

export interface PlannedCashEvent {
    Id: number;
    CompanyCode: string;
    BankID: string | null;
    EventDate: string;
    EventType: EventType;
    CurrencyCode: string;
    AmountFCY: number;
    Description: string | null;
    Confidence: Confidence;
    IsRecurring: boolean;
    RecurrencePattern: RecurrencePattern | null;
    RecurrenceEndDate: string | null;
    CounterpartyCompanyCode: string | null;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
}

export interface CreatePlannedEventData {
    companyCode: string;
    bankID?: string | null;
    eventDate: string;
    eventType: EventType;
    currencyCode: string;
    amountFCY: number;
    description?: string | null;
    confidence?: Confidence;
    isRecurring?: boolean;
    recurrencePattern?: RecurrencePattern | null;
    recurrenceEndDate?: string | null;
    counterpartyCompanyCode?: string | null;
}

export const GROUP_COMPANIES = ['bin', 'dbi', 'dch', 'dit', 'dsp', 'dtr', 'dfr', 'dpt'] as const;
export type GroupCompany = typeof GROUP_COMPANIES[number];

export type UpdatePlannedEventData = Partial<Omit<CreatePlannedEventData, 'companyCode'>>;

// ----- Cash Cockpit response shapes -----

export interface OverviewKPIs {
    totalCashEUR: number;
    accountCount: number;
    deltaMoM: number | null;
    deltaMoMPct: number | null;
    deltaYoY: number | null;
    deltaYoYPct: number | null;
}

export interface OverviewData {
    asOfDate: string;
    entities: string | string[];
    kpis: OverviewKPIs;
    trend24M: Array<{ yearMonth: string; totalEUR: number }>;
    trend24MByCurrency: Array<{ yearMonth: string; currencyCode: string; amountEUR: number }>;
    currencySplit: Array<{ currencyCode: string; amountFCY: number; amountEUR: number }>;
}

export interface ByEntityData {
    asOfDate: string;
    months: number;
    matrix: Array<{ companyCode: string; yearMonth: string; totalEUR: number }>;
    staleness: Array<{ companyCode: string; daysSinceLastEntry: number; severity: 'OK' | 'Warning' | 'Critical' }>;
}

export interface ForecastWeek {
    weekStart: string;
    weekEnd: string;
    weekNumber: number;
    openingCashEUR: number;
    arInflowEUR: number;
    apOutflowEUR: number;
    plannedInflowEUR: number;
    plannedOutflowEUR: number;
    netChangeEUR: number;
    closingCashEUR: number;
    isLimitedForecast: boolean;
    flags: string[];
}

export interface ForecastData {
    asOfDate: string;
    weeks: number;
    /**
     * Currency in which all *EUR-suffixed fields on ForecastWeek are expressed.
     * Default "EUR" (group-consolidated via FX rates). When non-EUR, the numbers
     * are native-currency totals filtered to that currency only (no FX conversion).
     */
    displayCurrency: string;
    forecast: ForecastWeek[];
}

export type ForecastFlowType = 'ar' | 'ap' | 'plannedIn' | 'plannedOut';

export interface ForecastBreakdownItem {
    date: string;
    companyCode: string;
    reference: string;
    party: string | null;
    description: string | null;
    amountFCY: number;
    currencyCode: string;
    amountInDisplayCurrency: number;
    extra: string | null;
}

export interface ForecastBreakdownData {
    weekStart: string;
    weekEnd: string;
    flowType: ForecastFlowType;
    displayCurrency: string;
    items: ForecastBreakdownItem[];
    total: number;
}

export interface FxExposureRow {
    companyCode: string;
    currencyCode: string;
    amountFCY: number;
    amountEUR: number;
    rateToEUR: number | null;
    shockUpEUR: number;
    shockDownEUR: number;
    shockDeltaEUR: number;
}

export interface FxExposureData {
    asOfDate: string;
    shockPct: number;
    data: FxExposureRow[];
}

export interface CounterpartyEntry {
    groupKey: string;
    bankIds: string[];
    accountCount: number;
    amountEUR: number;
    pctOfTotal: number;
}

export interface CounterpartyData {
    grandTotalEUR: number;
    concentrations: CounterpartyEntry[];
}

export interface CreditLineEntry {
    source: 'BankAccount' | 'Manual';
    id: number | null;
    companyCode: string;
    bankID: string;
    bankAccountName: string;
    bankCompanyName: string;
    currencyCode: string;
    creditLimit: number;
    utilisedFCY: number;
    headroomFCY: number;
    utilisationPct: number | null;
    interestRate: number | null;
    maturityDate: string | null;
    daysToMaturity: number | null;
    maturityAlert: 'None' | 'Warning' | 'Critical';
    renewalType: string | null;
    balanceDate: string | null;
    notes?: string | null;
}

export interface CreateCreditLineData {
    companyCode: string;
    facilityName: string;
    counterpartyName: string;
    currencyCode: string;
    creditLimit: number;
    utilisedAmount?: number;
    interestRate?: number | null;
    maturityDate?: string | null;
    renewalType?: 'Evergreen' | 'FixedTerm' | 'OnDemand' | null;
    bankID?: string | null;
    notes?: string | null;
}

export type UpdateCreditLineData = Partial<CreateCreditLineData> & { isActive?: boolean };

export interface IntercompanyLoan {
    source: 'BankAccount' | 'GL' | 'Manual';
    companyCode: string;
    bankID: string;
    bankAccountName: string;
    currencyCode: string;
    direction: 'Receivable' | 'Payable' | 'Unknown';
    counterparty: string | null;
    amountFCY: number;
    amountEUR: number;
    interestRate: number | null;
    maturityDate: string | null;
    balanceDate: string | null;
}

export interface IntercompanyGLMapping {
    id: number;
    companyCode: string;
    glAccountFrom: string;
    glAccountTo: string;
    counterpartyCompanyCode: string | null;
    direction: 'Receivable' | 'Payable' | 'Auto';
    description: string | null;
    isActive: boolean;
}

export interface CreateIntercompanyGLMapping {
    companyCode: string;
    glAccountFrom: string;
    glAccountTo: string;
    counterpartyCompanyCode?: string | null;
    direction: 'Receivable' | 'Payable' | 'Auto';
    description?: string | null;
}

export interface UpdateIntercompanyGLMapping {
    companyCode?: string;
    glAccountFrom?: string;
    glAccountTo?: string;
    counterpartyCompanyCode?: string | null;
    direction?: 'Receivable' | 'Payable' | 'Auto';
    description?: string | null;
    isActive?: boolean;
}

export interface DataQualitySignal {
    // 'ExcludedStale' = the account's newest balance is past the age limit, so it
    // no longer counts towards cash totals (api/src/shared/cash-staleness.ts).
    signalType: 'Staleness' | 'FxFallback' | 'OrphanAccount' | 'ExcludedStale';
    companyCode: string | null;
    companyName: string | null;
    reference: string;
    detail: string;
    severity: 'OK' | 'Warning' | 'Critical';
}

export interface OpsLogEntry {
    id: number;
    category: string;
    severity: string;
    title: string;
    detail: string | null;
    status: string;
    createdAt: string;
    resolvedAt: string | null;
}

export interface DataQualityData {
    summary: { total: number; critical: number; warning: number; ok: number };
    signals: DataQualitySignal[];
    opsLog: OpsLogEntry[];
}

export interface CockpitBankAccount {
    companyCode: string;
    bankID: string;
    bankAccountName: string;
    bankCompanyName: string;
    currencyCode: string | null;
    accountType: string;
    dataSource: string;
    isCashRelevant: boolean;
    isActive: boolean;
    groupId: number | null;
    groupName: string | null;
}

export interface CounterpartyGroup {
    id: number;
    groupName: string;
    description: string | null;
    sortOrder: number;
    isActive: boolean;
    memberBankIds: string[];
    memberCount: number;
}

export interface CreateCounterpartyGroupData {
    groupName: string;
    description?: string;
    sortOrder?: number;
    memberBankIds?: string[];
}

export interface UpdateCounterpartyGroupData {
    groupName?: string;
    description?: string;
    sortOrder?: number;
    isActive?: boolean;
    memberBankIds?: string[];
}
